import React, { useState, useCallback } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

import useWarrantyInfo from 'hooks/useWarrantyInfo'
import useSearchParams from 'hooks/useSearchParams'

import Search from 'components/Search'
import Spinner from 'components/Spinner'

import WarrantyInfo from 'pages/shared/WarrantyInfo'

import GA4 from 'helpers/ga4'

import { PageTitle, PageHeader, EmptyListMessage } from 'styles/shared'

const WarrantyLookup = () => {
  const navigate = useNavigate()

  const [refresh, setRefresh] = useState(Date.now())

  const query = useSearchParams('q')

  const [searchValue, setSearchValue] = useState<string>(query)
  const [searchedValue, setSearchedValue] = useState<string | null>(query)
  const [showSpinner, setShowSpinner] = useState(false)
  const { warrantyInfo, isLoaded, hasError } = useWarrantyInfo(searchValue, false, refresh)

  const onSearchCallback = useCallback((query: string) => {
    setShowSpinner(true)
    if (query.length) {
      setSearchedValue(query)
      setRefresh(Date.now())
    }

    GA4.sendEvent({ category: 'Warranty Lookup', action: 'Clicked on Search' })

    navigate(`/warranty_lookup${query ? `?q=${query}` : ''}`)
    setShowSpinner(false)
  }, [navigate])

  const renderContent = useCallback(() => {
    if (!searchedValue) {
      return
    }

    if (!isLoaded) {
      return <Spinner variant="page" />
    } else if (hasError) {
      return (
        <EmptyListMessage>
          <h1>Server Error</h1>
          <h2>An error occurred attempting to retrieve the warranty information</h2>
        </EmptyListMessage>
      )
    } else if (!warrantyInfo) {
      return (
        <EmptyListMessage>
          <h1>No Results</h1>
          <h2>No warranty information found</h2>
        </EmptyListMessage>
      )
    } else {
      return (
        <S.CardContainer>
          <WarrantyInfo serialNumber={searchedValue} warrantyInfo={warrantyInfo} />
        </S.CardContainer>
      )
    }
  }, [searchedValue, isLoaded, hasError, warrantyInfo])

  return (
    <S.Container>
      <PageHeader>
        <PageTitle>Warranty Lookup</PageTitle>

        <S.Search
          value={searchValue}
          focusOnMount
          placeholder="Search by serial number"
          ariaLabel="warranty lookup"
          onChange={setSearchValue}
          onSearch={onSearchCallback}
          onClickClearIcon={() => { setSearchValue(''); setSearchedValue(null); setShowSpinner(false) }}
          showSpinner={showSpinner}
        />
      </PageHeader>

      {renderContent()}
    </S.Container>
  )
}

export default WarrantyLookup

const S = {
  Container: styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
  `,
  CardContainer: styled.div`
    margin: 20px;
  `,
  Search: styled(Search)`
    max-width: 300px;
  `
}
